import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { CFG, GENERAL_SIZE, AUTH_SIZE, modalCSS } from "./shared";
import type { EpgSize } from "./shared";

type EpgMessageData = {
  m?: string;
  // Optional redirect URL from terms.php on Accept. Populated by
  // LW_Generate_Terms_JWT v1.8+ (Plaid brokers get a Plaid hosted-link URL,
  // Finicity brokers get a Finicity Connect URL). When present we navigate
  // the parent page to it instead of just closing the modal.
  r?: string;
};

declare global {
  interface Window {
    openEpgModal?: (url: string) => void;
    epg_auth?: (url: string) => void;
    [key: string]: unknown;
  }
}

const EpgModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [animateIn, setAnimateIn] = useState(false);
  const [size, setSize] = useState<EpgSize>(GENERAL_SIZE);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = modalCSS;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const open = (nextUrl: string, nextSize: EpgSize) => {
      setSize(nextSize);
      setUrl(nextUrl);
      setIsOpen(true);
      requestAnimationFrame(() => setAnimateIn(true));
    };

    // General modal (terms, IBV, etc.)
    window.openEpgModal = (nextUrl: string) => open(nextUrl, GENERAL_SIZE);

    // Payment-authorization clickwrap (auth.php) — compact auth size
    window.epg_auth = (nextUrl: string) => open(nextUrl, AUTH_SIZE);

    return () => {
      delete window.openEpgModal;
      delete window.epg_auth;
    };
  }, []);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (!e.origin.includes("ecommission.com")) return;
      const data = e.data as EpgMessageData;

      // r takes precedence — navigate the parent page (modal disappears with
      // the page change, so skip the explicit closeModal to avoid a stray
      // fade-out racing the navigation).
      if (data.r) {
        window.location.href = data.r;
        return;
      }
      if (data.m) closeModal();
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const closeModal = () => {
    setAnimateIn(false);
    setTimeout(() => {
      setIsOpen(false);
      setUrl(null);
    }, CFG.fadeDuration);
  };

  if (!isOpen || !url) return null;

  return ReactDOM.createPortal(
    <div
      className={`epg-modal-backdrop ${animateIn ? "epg-show" : ""}`}
    >
      <div
        className={`epg-modal-dialog ${animateIn ? "epg-show" : ""}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: size.width,
          maxWidth: CFG.maxWidth,
        }}
      >
        <div className="epg-modal-content">
          <div className="epg-modal-container">
            <iframe
              id={size.id}
              src={url}
              style={{
                width: "100%",
                height: size.iframeHeight,
                border: 0,
              }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EpgModal;
